<template>
    <main class="max-w-5xl m-5 mt-0 mb-10 w-full p-1 flex flex-col gap-3">
        <div class="sticky top-toolbar bg-white z-50 p-5">
            <div class="flex flex-col gap-3">
                <div class="flex justify-between flex-col-reverse sm:flex-row sm:items-center">
                    <div class="flex sm:items-center flex-col sm:flex-row">
                        <table class="mx-2">
                            <tr>
                                <th scope="row" class="text-right text-sm whitespace-nowrap">
                                    {{ $t('commons.version') }} :
                                </th>
                                <td class="px-3">
                                    <app-select
                                        :label="$t('commons.version')"
                                        :value="invoice.id"
                                        class=""
                                        :show-label="false"
                                        :disabled="!invoice.id"
                                        @change="onVersionSelected($event)"
                                    >
                                        <option
                                            :value="version.id"
                                            v-for="(version, index) in invoice.versions"
                                            :key="version.id"
                                            :selected="selectedVersion.id === version.id"
                                        >
                                            {{ invoice.versions.length - index }} ({{
                                                version.createdAt | humanizeDate
                                            }})
                                        </option>
                                    </app-select>
                                </td>
                            </tr>
                        </table>
                        <div class="m-2 sm:my-0">
                            <app-button @click="onNewVersion" :disabled="!invoice.id">
                                {{ $t('commons.newVersion') }}
                            </app-button>
                        </div>
                    </div>
                </div>
                <app-tabs
                    :useIdAsRouteName="false"
                    :values="values"
                    :selectedId="selectedValue"
                    @select="selectedValue = $event.id"
                    labelProp="label"
                />
            </div>
        </div>
        <invoice-form v-if="selectedValue === 'infos'"></invoice-form>
        <invoice-reviews-form v-if="selectedValue === 'reviews'"></invoice-reviews-form>
        <invoice-situation-form v-if="selectedValue === 'situation'"></invoice-situation-form>
        <app-quick-actions :options="quickActions" @choose="$event.run()"></app-quick-actions>
    </main>
</template>

<script>
import { updateBreadCrumbs } from '@/state/state';
import AppQuickActions from '../../components/appQuickActions/AppQuickActions';
import InvoiceForm from './InvoiceForm';
import { createInvoice, getInvoice, removeInvoice } from './invoices.service';
import { confirm } from '@/features/dialogs/dialogs.service';
import { getProject } from '@/features/projects/projects.service';
import AppButton from '@/components/appButton/AppButton.vue';
import { combineLatest } from 'rxjs';
import AppSelect from '@/components/appSelect/AppSelect.vue';
import AppTabs from '@/components/appTabs/AppTabs.vue';
import InvoiceReviewsForm from '@/features/invoices/InvoiceReviewsForm.vue';
import InvoiceSituationForm from '@/features/invoices/InvoiceSituationForm.vue';

export default {
    components: {
        InvoiceSituationForm,
        InvoiceReviewsForm,
        AppTabs,
        AppSelect,
        AppButton,
        InvoiceForm,
        AppQuickActions,
    },

    async created() {
        this.init();
    },
    watch: {
        $route: function () {
            this.init();
        },
    },
    methods: {
        init() {
            localStorage.setItem(
                'invoice.lastVisitedInvoiceId.' + this.$route.params.projectId,
                this.$route.params.invoiceId,
            );
            this.subscriptions = [
                combineLatest([
                    getProject(this.$route.params.projectId),
                    getInvoice(this.$route.params.projectId, this.$route.params.invoiceId),
                ]).subscribe(([project, invoice]) => {
                    this.project = project;
                    this.invoice = invoice;
                    this.isLastVersion =
                        invoice.versions.findIndex((version) => version.id === this.$route.params.invoiceId) === 0;

                    this.selectedVersion = this.$route.params.invoiceId;
                    this.isAdmin = project.me.allowedFeatures.includes('project_invoices');
                    this.isEmitter = project.me.bundleIds.includes(invoice.emitterId);
                    updateBreadCrumbs({
                        invoiceName: invoice.name,
                    });
                }),
            ];
        },
        async onVersionSelected(invoiceId) {
            await this.$router.push({
                name: this.$route.name,
                params: {
                    projectId: this.$route.params.projectId,
                    invoiceId,
                },
            });
        },
        async onNewVersion() {
            const invoiceEntity = {
                groupId: this.invoice.groupId,
                object: this.invoice.object,
                code: this.invoice.code,
                emissionDate: null,
                emitterId: this.invoice.emitterId,
            };
            const result = await createInvoice(this.$route.params.projectId, invoiceEntity);
            await this.$router.push({
                name: this.$route.name,
                params: {
                    projectId: this.$route.params.projectId,
                    invoiceId: result.id,
                },
            });
        },
        async removeInvoice() {
            if (await confirm(this.$t('commons.confirmMessage'))) {
                removeInvoice(this.$route.params.projectId, this.$route.params.invoiceId);
                if (this.invoice.versions.length > 1) {
                    this.$router.push({
                        name: this.$route.name,
                        params: {
                            projectId: this.$route.params.projectId,
                            invoiceId: this.invoice.versions[1].id,
                        },
                    });
                } else {
                    this.$router.push({
                        name: this.$route.meta.parentName,
                        params: {
                            projectId: this.$route.params.projectId,
                        },
                    });
                }
            }
        },
        async createNew() {
            const result = await createInvoice(this.$route.params.projectId, {
                name: '',
            });
            await this.$router.push({
                name: this.$route.name,
                params: {
                    projectId: this.$route.params.projectId,
                    invoiceId: result.id,
                },
            });
        },
    },
    data() {
        return {
            subscriptions: [],
            invoice: { versions: [] },
            project: null,
            isAdmin: false,
            isEmitter: false,
            isLastVersion: false,
            selectedVersion: null,
            values: [
                { id: 'infos', label: this.$t('invoices.infos') },
                { id: 'reviews', label: this.$t('invoices.reviewsTitle') },
                { id: 'situation', label: this.$t('invoices.situation') },
            ],
            selectedValue: 'infos',
        };
    },
    computed: {
        quickActions() {
            if (!this.isAdmin) {
                return [];
            }
            const defaultOptions = [
                {
                    name: this.$t('invoices.newInvoice'),
                    run: () => this.createNew(),
                },
                {
                    name: this.$t('commons.newVersion'),
                    run: () => this.onNewVersion(),
                },
            ];
            if (
                this.invoice.versions &&
                this.invoice.versions.length > 0 &&
                this.invoice.versions[0].id === this.invoice.id
            ) {
                defaultOptions.push({
                    name:
                        this.invoice.versions.length === 1
                            ? this.$t('commons.removeThisGroup')
                            : this.$t('commons.removeThisVersion'),
                    run: async () => {
                        this.removeInvoice();
                    },
                });
            }
            return defaultOptions;
        },
    },
};
</script>
